import React, {
  ComponentProps,
  forwardRef,
  Ref,
  useCallback,
  useState,
} from "react";
import { colors, ExtractVariants, spacing } from "../../stitches.config";
import {
  ButtonContentsWrapper,
  LoadingWrapper,
  StyledButton,
} from "./__styles__/Button";
import { Icon, IconNames } from "./Icons/LucideIcons";

export const BUTTON_STYLE_VARIANTS = [
  "primary",
  "secondary",
  "outlineLight",
  "outlineDark",
  "ghost",
  "ghostWhite",
  "ghostGrey",
  "ghostAlert",
  "alert",
  "hoverOutline",
] satisfies ExtractVariants<typeof StyledButton>["styleVariant"][];

export const BUTTON_SIZES = [
  "xSmall",
  "small",
  "medium",
] satisfies ExtractVariants<typeof StyledButton>["size"][];

export type StyleVariant = (typeof BUTTON_STYLE_VARIANTS)[number];

const IconColorMap: Record<StyleVariant | "disabled", keyof typeof colors> = {
  primary: "contentPrimaryInverted",
  secondary: "contentPrimary",
  outlineLight: "contentPrimary",
  outlineDark: "contentPrimaryInverted",
  hoverOutline: "contentPrimary",
  ghost: "contentInfo",
  ghostWhite: "contentPrimaryInverted",
  ghostGrey: "contentSecondary",
  ghostAlert: "contentCritical",
  alert: "contentPrimaryInverted",
  disabled: "contentDisabled",
} as const;

export type ButtonProps = Omit<
  ComponentProps<typeof StyledButton>,
  "css" | "styleVariant"
> & {
  styleVariant: StyleVariant;
  leftIconName?: IconNames;
  loading?: boolean;
  rightIconName?: IconNames;
  iconSpacing?: keyof typeof spacing;
  readOnly?: boolean;
};

const Button = forwardRef(
  (
    {
      leftIconName,
      loading,
      rightIconName,
      styleVariant,
      iconSpacing,
      readOnly,
      ...props
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const disabledState = props.disabled || loading || readOnly;
    const [isHovered, setIsHovered] = useState(false);
    const getIconColor = useCallback(
      (styleVariant: StyleVariant) => {
        if (isHovered && !disabledState && styleVariant === "ghost") {
          return "blue600";
        }

        if (
          (isHovered && !disabledState && styleVariant === "ghostWhite") ||
          styleVariant === "ghostGrey"
        ) {
          return "contentSecondaryInverted";
        }

        return IconColorMap[styleVariant];
      },
      [disabledState, isHovered],
    );

    const [iconColor, setIconColor] = useState<keyof typeof colors>(
      getIconColor(styleVariant),
    );

    React.useEffect(() => {
      setIconColor(getIconColor(styleVariant));
    }, [isHovered, styleVariant, disabledState, getIconColor]);

    let contents: React.ReactNode;

    if (loading) {
      contents = (
        <LoadingWrapper>
          <Icon
            iconName="loading"
            aria-label="loading"
            color={iconColor}
            size={16}
          />
        </LoadingWrapper>
      );
    } else {
      const wrapperStyle =
        iconSpacing ? { gap: spacing[iconSpacing].value } : {};
      contents = (
        <ButtonContentsWrapper style={wrapperStyle}>
          {leftIconName && (
            <Icon iconName={leftIconName} color={iconColor} size={16} />
          )}
          {props.children}
          {rightIconName && (
            <Icon iconName={rightIconName} color={iconColor} size={16} />
          )}
        </ButtonContentsWrapper>
      );
    }

    return (
      <StyledButton
        {...props}
        ref={ref}
        styleVariant={styleVariant}
        disabled={props.disabled || loading || readOnly}
        disabledStyles={props.disabled && !loading}
        onMouseOver={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        tabIndex={0}
      >
        {contents}
      </StyledButton>
    );
  },
);

// including this so we can view the component name instead of forwardRef
// in our tooling (React DevTools, etc)
Button.displayName = "Button";

export { Button };
